import type { Measurement } from "./schemas/measurement.js";

// Per-field "current value" resolution for the measurements time series.
//
// Model C (save-only-what-you-measured) makes entries SPARSE: a quick
// weigh-in carries only weightKg, a tape day only the circumferences you
// took. So the "current" value of each field is the most recent ENTRY
// THAT CARRIES IT — not simply items[0], which under sparse entries would
// hide a waist you measured last week behind today's weight-only row.
//
// takenAt is an ISO-8601 string in a fixed format, so a lexicographic
// compare orders entries chronologically without parsing — and we don't
// assume the input is pre-sorted.

export type MeasureFieldKey =
  | "weightKg"
  | "heightCm"
  | "neckCm"
  | "chestCm"
  | "waistCm"
  | "hipsCm"
  | "bicepsLCm"
  | "bicepsRCm"
  | "forearmsLCm"
  | "forearmsRCm"
  | "thighsLCm"
  | "thighsRCm"
  | "calvesLCm"
  | "calvesRCm";

export const MEASURE_FIELD_KEYS: MeasureFieldKey[] = [
  "weightKg",
  "heightCm",
  "neckCm",
  "chestCm",
  "waistCm",
  "hipsCm",
  "bicepsLCm",
  "bicepsRCm",
  "forearmsLCm",
  "forearmsRCm",
  "thighsLCm",
  "thighsRCm",
  "calvesLCm",
  "calvesRCm",
];

export type LatestValue = { value: number; takenAt: string };
export type LatestByField = Partial<Record<MeasureFieldKey, LatestValue>>;

// For each measurable field, the value + date from the most recent entry
// that carries it.
export function latestByField(items: Measurement[]): LatestByField {
  const out: LatestByField = {};
  for (const m of items) {
    for (const k of MEASURE_FIELD_KEYS) {
      const v = m[k];
      if (typeof v !== "number") continue;
      const cur = out[k];
      if (cur === undefined || m.takenAt > cur.takenAt) {
        out[k] = { value: v, takenAt: m.takenAt };
      }
    }
  }
  return out;
}
